import { api } from "./client";
import type {
  ChatSession,
  FilterOption,
  FilterValues,
  Report,
  ReportRunResult,
  Visibility,
} from "./types";

// Wraps the /api/reports/* endpoints. See `internal/api/handlers/reports.go`
// and the Chat/Report split spec for the contract.

export function listReports(): Promise<Report[]> {
  return api.get<Report[]>("/api/reports");
}

export function getReport(id: number): Promise<Report> {
  return api.get<Report>(`/api/reports/${id}`);
}

// createReportFromMessage turns ONE chat message's выгрузка (its SQL/view) into
// a new, self-contained report. The report has no chat binding.
export function createReportFromMessage(
  messageId: number,
  name: string,
): Promise<Report> {
  return api.post<Report>("/api/reports", { message_id: messageId, name });
}

export interface RunReportParams {
  dateFrom?: string;
  dateTo?: string;
  filters?: FilterValues;
  page?: number;
  pageSize?: number;
}

export function runReport(
  id: number,
  params: RunReportParams = {},
  signal?: AbortSignal,
): Promise<ReportRunResult> {
  const body: Record<string, unknown> = {};
  if (params.dateFrom) body.date_from = params.dateFrom;
  if (params.dateTo) body.date_to = params.dateTo;
  // Send only filters that actually have selected values, so an untouched
  // filter stays inactive (backend expands it to a no-op).
  if (params.filters) {
    const active: FilterValues = {};
    for (const [id, values] of Object.entries(params.filters)) {
      if (values.length > 0) active[id] = values;
    }
    if (Object.keys(active).length > 0) body.filters = active;
  }
  body.page = params.page ?? 1;
  body.page_size = params.pageSize ?? 100;
  return api.post<ReportRunResult>(`/api/reports/${id}/run`, body, { signal });
}

export function getFilterOptions(
  reportId: number,
  filterId: string,
): Promise<FilterOption[]> {
  return api.get<FilterOption[]>(
    `/api/reports/${reportId}/filters/${encodeURIComponent(filterId)}/options`,
  );
}

// saveReport overwrites the report's definition with one editor-chat message's
// выгрузка. The message must come from an editor chat bound to this report.
// expectedVersion is the report.version the caller last saw — mismatch returns
// 409 version_conflict with ApiError.currentVersion populated.
export function saveReport(
  id: number,
  messageId: number,
  expectedVersion: number,
): Promise<Report> {
  return api.post<Report>(`/api/reports/${id}/save`, {
    message_id: messageId,
    expected_version: expectedVersion,
  });
}

// listReportChats returns the caller's editor chats for a report.
export function listReportChats(reportId: number): Promise<ChatSession[]> {
  return api.get<ChatSession[]>(`/api/reports/${reportId}/chats`);
}

// createReportChat opens a new editor chat bound to a report.
export function createReportChat(reportId: number): Promise<ChatSession> {
  return api.post<ChatSession>(`/api/reports/${reportId}/chats`, {});
}

// deleteReportChat removes a single editor chat. The report itself is
// self-contained and is unaffected — only this chat and its messages are gone.
export function deleteReportChat(chatId: number): Promise<void> {
  return api.delete<void>(`/api/chat/sessions/${chatId}`);
}

export function renameReport(id: number, name: string): Promise<Report> {
  return api.put<Report>(`/api/reports/${id}/rename`, { name });
}

export function deleteReport(id: number): Promise<void> {
  return api.delete<void>(`/api/reports/${id}`);
}

// setReportVisibility publishes (public) or unpublishes (private) a report.
// Author or workspace owner only — the backend enforces this.
export function setReportVisibility(
  id: number,
  visibility: Visibility,
): Promise<Report> {
  return api.post<Report>(`/api/reports/${id}/visibility`, { visibility });
}

// duplicateReport forks a readable report into a private copy owned by the
// caller. Editor chats are created on demand when the copy is edited.
export function duplicateReport(id: number): Promise<Report> {
  return api.post<Report>(`/api/reports/${id}/duplicate`, {});
}

// setReportFavorite stars/unstars a report for the current user only. Any
// readable report can be favorited — it's a bookmark, not a report mutation.
export function setReportFavorite(id: number, favorite: boolean): Promise<void> {
  return api.post<void>(`/api/reports/${id}/favorite`, { favorite });
}
